import { useMemo } from 'react';
import { useAppViewModel } from '../viewmodels/AppViewModel';
import { HeroSection, FeatureItem, MetricItem, FAQItem, SectionContent } from '../types';

/**
 * Hook customizado para acessar conteúdo da aplicação
 * Segue o princípio da Responsabilidade Única (SRP)
 */
export const useContent = () => {
  const { content, isLoading, error } = useAppViewModel();

  // Memoização para otimizar performance
  const memoizedContent = useMemo(() => {
    if (!content) return null;

    return {
      hero: content.hero,
      problem: content.problem,
      valueProposition: content.valueProposition,
      benefits: content.benefits,
      howItWorks: content.howItWorks,
      metrics: content.metrics,
      mission: content.mission,
      faq: content.faq
    };
  }, [content]);

  // Helpers para acessar seções específicas
  const getHeroContent = (): HeroSection | null => {
    return memoizedContent?.hero || null;
  };

  const getFeatures = (sectionKey: keyof typeof memoizedContent): FeatureItem[] => {
    const section = memoizedContent?.[sectionKey] as unknown as SectionContent;
    return section?.items || [];
  };

  const getMetrics = (): MetricItem[] => {
    return memoizedContent?.metrics?.items || [];
  };

  const getFAQItems = (): FAQItem[] => {
    return memoizedContent?.faq?.items || [];
  };

  const getSectionContent = (sectionKey: keyof typeof memoizedContent): SectionContent | null => {
    return memoizedContent?.[sectionKey] as unknown as SectionContent || null;
  };

  return {
    content: memoizedContent,
    isLoading,
    error,
    // Helpers
    getHeroContent,
    getFeatures,
    getMetrics,
    getFAQItems,
    getSectionContent
  };
};

/**
 * Hook para acessar navegação
 */
export const useNavigation = () => {
  const { navigation, isLoading, error } = useAppViewModel();

  const memoizedNavigation = useMemo(() => {
    return navigation;
  }, [navigation]);

  return {
    navigation: memoizedNavigation,
    isLoading,
    error
  };
};